import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

import { supabase } from '../../../lib/supabase';

const BadgeManagementModal = ({ user, onClose, onUpdate }) => {
  const [achievements, setAchievements] = useState([]);
  const [allBadges, setAllBadges] = useState([]);
  const [selectedBadgeIds, setSelectedBadgeIds] = useState([]); // Track selected badge IDs
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Fetch badges every time modal opens
    fetchBadges();
    
    if (user?.id) {
      loadUserBadges();
    }
  }, []); // Empty dependency array ensures fresh fetch on every mount

  const fetchBadges = async () => {
    try {
      const { data, error } = await supabase?.from('badges')?.select('id, name, icon, description, category, target_count, admin_only')?.order('name');
      
      console.log('Fetched badges:', data); // Debug log
      
      if (error) throw error;
      setAllBadges(data || []);
    } catch (err) {
      console.error('Fetch badges error:', err);
      setError(err?.message || 'Rozetler yüklenemedi');
    }
  };

  const loadUserBadges = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch user's badges from user_badges table (contains proper UUIDs)
      const { data: userBadges, error: badgesError } = await supabase
        ?.from('user_badges')
        ?.select('badge_id')
        ?.eq('user_id', user?.id);
      
      if (badgesError) throw badgesError;
      
      // Extract badge UUIDs from the result
      const badgeIds = userBadges?.map(ub => ub?.badge_id) || [];
      console.log('User badge UUIDs:', badgeIds); // Debug log
      
      setSelectedBadgeIds(badgeIds);
    } catch (err) {
      console.error('Load user badges error:', err);
      setError(err?.message || 'Kullanıcı rozetleri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // Merge database badges with user's selected badges
  const mergedBadges = allBadges?.map(badge => {
    const isSelected = selectedBadgeIds?.includes(badge?.id);
    
    return {
      id: badge?.id,
      name: badge?.name,
      description: badge?.description,
      icon: badge?.icon,
      category: badge?.category,
      target: badge?.target_count || 1,
      adminOnly: badge?.admin_only || false,
      unlocked: isSelected, // Use selectedBadgeIds as source of truth
      progress: 0, // Progress calculation would require additional query
      unlockedDate: null
    };
  });

  const handleToggleBadge = async (badgeId, currentlyUnlocked) => {
    // Update selectedBadgeIds array
    let newSelectedIds;
    if (currentlyUnlocked) {
      // Remove badge from selection
      newSelectedIds = selectedBadgeIds?.filter(id => id !== badgeId);
    } else {
      // Add badge to selection
      newSelectedIds = [...selectedBadgeIds, badgeId];
    }
    
    setSelectedBadgeIds(newSelectedIds);
    
    // Immediately sync with database
    await syncBadges(newSelectedIds);
  };

  const syncBadges = async (badgeIds) => {
    setLoading(true);
    setError('');
    
    try {
      // Call the RPC function to sync badges
      const { error: rpcError } = await supabase?.rpc('sync_user_badges', {
        target_user_id: user?.id,
        new_badge_ids: badgeIds // Array of UUIDs
      });
      
      if (rpcError) throw rpcError;
      
      // State is already updated via setSelectedBadgeIds in handleToggleBadge
      // No need to re-fetch - this was causing the stale state issue
      
      // Notify parent to refresh user data
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error('Sync badges error:', err);
      setError(err?.message || 'Rozetler senkronize edilemedi');
      // On error, reload to get accurate state from database
      await loadUserBadges();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-organic max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground flex items-center gap-2">
              <Icon name="Award" size={24} />
              Başarı Rozeti Yönetimi
            </h2>
            <p className="text-sm text-muted-foreground mt-1">{user?.name}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            onClick={onClose}
            disabled={loading}
          />
        </div>

        <div className="p-4 md:p-6 space-y-4">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-4">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
          
          <div className="bg-muted/30 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
              <Icon name="Info" size={16} />
              Bilgi
            </h3>
            <p className="text-xs text-muted-foreground">
              Kullanıcılara rozet verebilir veya mevcut rozetleri iptal edebilirsiniz.
              Değişiklikler anında kaydedilir.
            </p>
          </div>

          {loading && mergedBadges?.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-sm text-muted-foreground">Rozetler yükleniyor...</p>
            </div>
          ) : mergedBadges?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="Award" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Henüz rozet tanımlanmamış</p>
            </div>
          ) : (
            mergedBadges?.map((badge) => {
              return (
                <div
                  key={badge?.id}
                  className={`rounded-lg border-2 p-4 transition-smooth ${
                    badge?.unlocked
                      ? 'bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/30' :'bg-muted/30 border-border'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-2xl ${
                        badge?.unlocked
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {badge?.icon}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-foreground mb-1">
                            {badge?.name}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {badge?.description}
                          </p>
                          {badge?.category && (
                            <span className="inline-block mt-2 text-xs bg-muted px-2 py-1 rounded">
                              {badge?.category}
                            </span>
                          )}
                          {badge?.adminOnly && (
                            <span className="inline-flex items-center gap-1 mt-2 ml-2 text-xs bg-primary/20 text-primary px-2 py-1 rounded">
                              <Icon name="Shield" size={12} />
                              Sadece Admin
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-4">
                          <div className="text-sm">
                            <span className="text-muted-foreground">İlerleme: </span>
                            <span className="font-semibold text-foreground">
                              {badge?.progress}/{badge?.target}
                            </span>
                          </div>
                          {badge?.unlocked && (
                            <div className="flex items-center gap-1 text-success text-sm">
                              <Icon name="Check" size={14} />
                              <span>Kazanıldı</span>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={badge?.unlocked}
                            onChange={() => handleToggleBadge(badge?.id, badge?.unlocked)}
                            disabled={loading}
                            label=""
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-4 md:p-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Kapat
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BadgeManagementModal;
